import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';
import { getRegistrationStatus } from '../../redux/actions/auth';
import { getIsUserInPhiloSupportedGeoRegion } from '../../redux/selectors/auth';
import { getHaveValidLoginURLs, getHaveValidRegisterURLs } from '../../redux/selectors/authConfig';
import {
  AUTHENTICATE_NESTED,
  AUTHORIZE_NESTED,
  GEO_FENCE_NESTED,
  SIGNUP_NESTED,
  SUBSCRIBE_NESTED,
  isValidAppPath,
} from '../../utils/router';
import useCheckLoginStatus from '../auth/use-check-login-status';
import useInAppNavigate from '../router/use-in-app-navigate';
import useAppDispatch from '../use-app-dispatch';

/** KU
 *
 * This app-wide hook checks user's authentication status every time the in-app
 * location changes. Specifically, it checks if the user is already logged if
 * they visit `/authorize` or `authenticate`, and checks if they're already a
 * registered user if they visit `/subscribe`. This hook also handles
 * redirecting the user to `/subscribe` whenever they visit an unsupported URL.
 */
export default function useCheckAuthStatus() {
  const { pathname: currentPath } = useLocation();
  const isUserInPhiloSupportedGeoRegion = useSelector(getIsUserInPhiloSupportedGeoRegion);
  const haveValidAuthApiURLs = useSelector(getHaveValidLoginURLs);
  const haveValidRegisterApiURLs = useSelector(getHaveValidRegisterURLs);

  const dispatch = useAppDispatch();
  const navigate = useInAppNavigate();
  const { handleCheckLoginStatus } = useCheckLoginStatus();

  const isAuthenticatePath = !!useMatch(AUTHENTICATE_NESTED);
  const isAuthorizePath = !!useMatch(AUTHORIZE_NESTED);
  const isSubscribePath = isValidAppPath(currentPath, [SUBSCRIBE_NESTED, SIGNUP_NESTED]);

  useEffect(() => {
    if (!isUserInPhiloSupportedGeoRegion && currentPath !== GEO_FENCE_NESTED) {
      navigate(GEO_FENCE_NESTED, { replace: true });
      return;
    }

    const areAllURLsInvalid = !haveValidAuthApiURLs && !haveValidRegisterApiURLs;
    if (!isValidAppPath(currentPath)) {
      navigate(SUBSCRIBE_NESTED, { replace: true });
    } else if (isAuthenticatePath && !haveValidAuthApiURLs) {
      handleCheckLoginStatus();
    } else if (isSubscribePath && !haveValidRegisterApiURLs) {
      dispatch(getRegistrationStatus());
    } else if (isAuthorizePath && areAllURLsInvalid) {
      // GW: if a reload happens at /authorize and there no api urls,
      // assume /authenticate path and check status from there (for now).
      // TODO: JD: Use session storage to better track login state if reloading /authorize.
      handleCheckLoginStatus();
    }
  }, [currentPath]);
}
